import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom"; // Changed from useNavigate to Link
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCategories } from "../../hooks/useCategories";
import skillystrilogo from "../../assets/d7bc748335e39f55a86f420bc064a0c9ff3ffd30.png";

import Image1 from '../../assets/howItWorks/how to work image ver 1.png'
import Image2 from '../../assets/howItWorks/how to work image ver 2.png'
import Image3 from '../../assets/howItWorks/how to work image ver 3.png'


type ScrollDirection = "left" | "right";

const SKELETON_CATEGORIES = Array.from({ length: 8 }, (_, i) => ({
  id: `skeleton-${i}`,
  name: "",
  icon: null,
  iconColor: "#3B82F6",
}));

export function CategoriesHomepage() {
  const { categories, loading, error } = useCategories();
  
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [visibleSteps, setVisibleSteps] = useState<Set<number>>(new Set());
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  // 🟢 NEW: Active Step State for the dynamic vertical timeline and carousel sync
  const [activeStep, setActiveStep] = useState(0);

  // 🟢 NEW: Premium placeholder mockup graphics mapping onto steps 1, 2, and 3
  const carouselImages = [
    Image1, // Step 1 asset
    Image2, // Step 2 asset
    Image3  // Step 3 asset
  ];

  const displayCategories = loading || error || categories.length === 0 
    ? SKELETON_CATEGORIES 
    : categories;

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setShowLeftArrow(container.scrollLeft > 0);
    setShowRightArrow(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 10
    );
  };

  const scroll = (direction: ScrollDirection) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 300;
    const newScrollLeft =
      direction === "left"
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const timer = setTimeout(checkScroll, 100);
    window.addEventListener("resize", checkScroll);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkScroll);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.getAttribute("data-step") || "0");
          if (entry.isIntersecting) {
            setVisibleSteps((prev) => new Set([...prev, index]));
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -100px 0px",
      }
    );

    stepRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // 🟢 NEW: Self-playing interval driver syncing the active layout modules every 3.5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 3);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-12 sm:py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl text-gray-900 mb-3 font-semibold">
            Explore Categories
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            Find experts across various domains
          </p>
        </div>

        {/* Categories Carousel */}
        <div className="relative group">
          {/* Left Arrow */}
          {showLeftArrow && (
            <button
              onClick={() => scroll("left")}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 sm:p-3 hover:bg-gray-50 transition-all opacity-0 group-hover:opacity-100 hidden md:block"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>
          )}

          {/* Right Arrow */}
          {showRightArrow && (
            <button
              onClick={() => scroll("right")}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 sm:p-3 hover:bg-gray-50 transition-all opacity-0 group-hover:opacity-100 hidden md:block"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-gray-700" />
            </button>
          )}

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            onScroll={checkScroll}
            className="overflow-x-auto overflow-y-hidden pb-4 scrollbar-hide"
          >
            <div className="flex gap-4 sm:gap-6 justify-start md:justify-center min-w-min px-2 sm:px-4">
              {displayCategories.map((category) => {
                const isLoading = loading || !category.name;
                const routePath = !isLoading ? category.name.toLowerCase().trim() : "";
                
                return (
                  <Link
                    key={category.id}
                    // Configures dynamic route link, safely handles skeleton state fallback
                    to={isLoading ? "#" : `/experts/${routePath}`}
                    onClick={(e) => {
                      if (isLoading) e.preventDefault(); // Blocks anchor click routing if skeleton is loading
                    }}
                    className={`group/card relative block p-4 sm:p-6 bg-white rounded-2xl border-2 border-gray-200 hover:border-transparent hover:shadow-xl transition-all duration-300 flex-shrink-0 w-36 sm:w-44 lg:w-48 ${
                      isLoading ? "pointer-events-none select-none" : "cursor-pointer"
                    }`}
                  >
                    {/* Hover Background */}
                    <div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover/card:opacity-100 transition-opacity duration-300"
                      style={{
                        background: isLoading 
                          ? "linear-gradient(135deg, #E5E7EB33, #E5E7EB1A)"
                          : `linear-gradient(135deg, ${category.iconColor}33, ${category.iconColor}1A)`,
                      }}
                    />

                    <div className="relative space-y-2 sm:space-y-3">
                      {/* Icon Box */}
                      <div
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center mx-auto group-hover/card:scale-110 transition-transform duration-300 p-2"
                        style={{
                          background: isLoading
                            ? "linear-gradient(135deg, #E5E7EB33, #E5E7EB1A)"
                            : `linear-gradient(135deg, ${category.iconColor}33, ${category.iconColor}1A)`,
                        }}
                      >
                        {isLoading ? (
                          <div className="w-full h-full bg-gray-300 rounded-lg"></div>
                        ) : (
                          <img
                            src={category.icon ?? skillystrilogo}
                            alt={category.name}
                            className="w-full h-full object-contain"
                          />
                        )}
                      </div>

                      {/* Category Name */}
                      <div className="text-sm sm:text-base font-medium text-gray-900 text-center min-h-[20px] sm:min-h-[24px]">
                        {isLoading ? (
                          <div className="h-4 sm:h-5 bg-gray-300 rounded animate-pulse mx-auto w-3/4"></div>
                        ) : (
                          category.name
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 text-center">
              <p className="text-sm text-red-600">
                Unable to load categories. Mr. Developer working on this.
              </p>
            </div>
          )}
        </div>

        {/* How it Works Section */}
        <div className="mt-12 sm:mt-16">
          <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-6 sm:mb-10 text-center">
            How it Works
          </h2>

          {/* 🟢 FIXED: Transformed from 3-Column Grid to an interactive 2-Column Split Layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center mb-12 sm:mb-16 max-w-5xl mx-auto">
            
            {/* Left Box: Vertical Steps Stacking Container (7 Columns Wide on PC) */}
            <div className="md:col-span-7 space-y-3 sm:space-y-4">
              
              {/* Step 1 */}
              <div
                ref={(el) => {
                  stepRefs.current[0] = el;
                }}
                onClick={() => setActiveStep(0)}
                data-step="0"
                className={`flex items-start gap-4 p-4 sm:p-5 rounded-2xl border transition-all duration-500 cursor-pointer select-none ${
                  activeStep === 0
                    ? "bg-[#e0f2fe]/60 border-[#bae6fd] shadow-sm scale-[1.01]"
                    : "bg-transparent border-transparent hover:bg-white/50"
                } ${
                  visibleSteps.has(0) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: "0ms" }}
              >
                <div className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center font-bold text-base sm:text-lg transition-colors ${
                  activeStep === 0 ? "bg-[#083c61] text-white" : "bg-gray-200 text-gray-700"
                }`}>
                  1
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
                    Choose a Category
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    Browse through our verified categories
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div
                ref={(el) => {
                  stepRefs.current[1] = el;
                }}
                onClick={() => setActiveStep(1)}
                data-step="1"
                className={`flex items-start gap-4 p-4 sm:p-5 rounded-2xl border transition-all duration-500 cursor-pointer select-none ${
                  activeStep === 1
                    ? "bg-[#e0f2fe]/60 border-[#bae6fd] shadow-sm scale-[1.01]"
                    : "bg-transparent border-transparent hover:bg-white/50"
                } ${
                  visibleSteps.has(1) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: "150ms" }}
              >
                <div className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center font-bold text-base sm:text-lg transition-colors ${
                  activeStep === 1 ? "bg-[#083c61] text-white" : "bg-gray-200 text-gray-700"
                }`}>
                  2
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
                    Select a Verified Expert
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    Pick from our trusted professionals
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div
                ref={(el) => {
                  stepRefs.current[2] = el;
                }}
                onClick={() => setActiveStep(2)}
                data-step="2"
                className={`flex items-start gap-4 p-4 sm:p-5 rounded-2xl border transition-all duration-500 cursor-pointer select-none ${
                  activeStep === 2
                    ? "bg-[#e0f2fe]/60 border-[#bae6fd] shadow-sm scale-[1.01]"
                    : "bg-transparent border-transparent hover:bg-white/50"
                } ${
                  visibleSteps.has(2) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                }`}
                style={{ transitionDelay: "300ms" }}
              >
                <div className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center font-bold text-base sm:text-lg transition-colors ${
                  activeStep === 2 ? "bg-[#083c61] text-white" : "bg-gray-200 text-gray-700"
                }`}>
                  3
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
                    Start Confidential Consultation
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    Connect securely and privately
                  </p>
                </div>
              </div>
            </div>

            {/* 🟢 FIXED RIGHT BOX: Expanded height utilities locally to maximize vector illustration scale on PC */}
            <div className="md:col-span-5 h-[320px] sm:h-[400px] md:h-[440px] lg:h-[500px] w-full relative flex items-center justify-center">
              {carouselImages.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Step ${i + 1} Illustration`}
                  className={`absolute max-h-full max-w-full object-contain transition-all duration-700 ease-in-out ${
                    activeStep === i 
                      ? "opacity-100 scale-100 z-10" 
                      : "opacity-0 scale-95 z-0 pointer-events-none"
                  }`}
                />
              ))}
            </div>

          </div>

          {/* Privacy Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div className="space-y-4">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900">
                  Your Privacy Comes First
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex-shrink-0 text-blue-600">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="w-full h-full"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">
                      1-to-1 Private Consultations
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex-shrink-0 text-blue-600">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="w-full h-full"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">
                      No Data Sharing
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex-shrink-0 text-blue-600">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="w-full h-full"
                      >
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm sm:text-base text-gray-700">
                      Encrypted Chats
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center md:justify-end">
                <div className="w-24 h-24 sm:w-28 sm:h-28 bg-brand-blue-900 rounded-2xl flex items-center justify-center transform rotate-6 hover:rotate-12 transition-transform duration-300">
                  <svg
                    className="w-14 h-14 sm:w-16 sm:h-16 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}